import { computed, reactive, Ref, watch } from 'vue'
import { PickerItemType } from '@/components/vision/next/picker-list/models'
import {
  HeadlessAutoCompleteValueType,
  useAutoCompleteProvider,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'

export interface SearchOptions {
  searchText: string | null
}

type PickerOptions = {
  onNavigate?: () => void
  getScrollPosition?: () => number
  scrollToPosition?: (pos: number) => void
  valueResolver?: ValueResolver
  onValueChanged?: (item?: HeadlessAutoCompleteValueType) => void
  value: Ref<string | null>
  useSearchItems: (options: Ref<SearchOptions>) => {
    items: Ref<Readonly<PickerItemType[]>>
    hasNextPage: Ref<boolean>
    fetchNextPage: (options?: any) => any
    isFetching: Ref<boolean>
    isFetched: Ref<boolean>
    isInitialLoading: Ref<boolean>
  }
}

type State = {
  searchText: string
  scrollPosition: number | null
}

export const useNewAutocomplete = (options: PickerOptions) => {
  const current = reactive<State>({
    searchText: '',
    scrollPosition: null,
  })

  const search = computed(() => current.searchText)

  const autoComplete = useAutoCompleteProvider({
    search,
    onValueChanged: options.onValueChanged as any,
    value: options.value as any,
    valueResolver: options.valueResolver,
  })

  const searchOptions = computed<SearchOptions>(() => ({
    searchText: current.searchText,
  }))

  const {
    items,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  } = options.useSearchItems(searchOptions)

  // if the user is actively searching or changing
  // filters we should request to reset the scroll position
  watch(searchOptions, (newOpts, oldOpts) => {
    if (oldOpts.searchText !== newOpts.searchText) {
      options.scrollToPosition?.(0)
    }
  })

  const selectItem = (item: PickerItemType) => {
    autoComplete.addValue(item)
    current.scrollPosition = options.getScrollPosition?.() ?? 0
  }

  const updateSearch = (newSearch: string) => {
    current.searchText = newSearch
  }

  const continueFetching = () => {
    if (hasNextPage?.value && !isFetching.value) {
      fetchNextPage()
    }
  }

  const clear = () => {
    autoComplete.clear()

    current.searchText = ''
    current.scrollPosition = null

    options.scrollToPosition?.(0)
  }

  const noResultsMessage = computed(() => {
    if (isInitialLoading.value || !isFetched.value || items.value.length > 0) {
      return
    }
    return 'No results'
  })

  const clearSearch = () => {
    current.searchText = ''
  }

  return {
    items,
    updateSearch,
    selectItem,
    clear,
    clearSearch,
    search,
    continueFetching,
    noResultsMessage,
  }
}
